/* eslint-disable camelcase */
import { createContext, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { osName, getUA, browserName } from "react-device-detect";
// utils
import axios from "../utils/axios";
import { API_PATH } from "../utils/apis";
import { setSession, setHeader, setEmail, getEmail } from "../utils/jwt";
import IdempotencyManager from "../utils/idempotency";

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  isVerifyEmail: false,
  isPopupOpen: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, isVerifyEmail, user } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isVerifyEmail,
      isInitialized: true,
      isPopupOpen: false,
      user,
    };
  },
  UPDATE: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user, isVerifyEmail } = action.payload;
    return {
      ...state,
      isVerifyEmail,
      isAuthenticated: true,
      isPopupOpen: false,
      user,
    };
  },
  LOGOUT: (state, action) => {
    const { isPopUp } = action.payload;
    return {
      ...state,
      isAuthenticated: false,
      isVerifyEmail: false,
      isPopupOpen: isPopUp,
      user: null,
    };
  },
  VERIFY: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isVerifyEmail: true,
      user,
    };
  },
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isVerifyEmail: false,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext({
  ...initialState,
  method: "jwt",
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});
AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialize = async () => {
    setHeader();
    let isAuthenticated = false;
    let isVerifyEmail = false;
    let user = {};
    try {
      const response = await axios.get(
        `${API_PATH.profile}?email=${getEmail()}`
      );
      const { status, data } = response.data;
      user = data;
      if (status === "success") {
        const { profile } = user;
        isAuthenticated = Boolean(profile.uid);
        // eslint-disable-next-line
        if (isAuthenticated && parseInt(profile.is_verified, 10) === 1) {
          isVerifyEmail = true;
        }
      }

      if (
        status === "userid_expired" ||
        status === "userid_error" ||
        status === "invalid_profile"
      ) {
        isAuthenticated = false;
        setSession(null);
      }
    } catch (err) {
      // null
    }
    dispatch({
      type: "INITIALIZE",
      payload: {
        isAuthenticated,
        isVerifyEmail,
        user,
      },
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  const logout = async (isPopUp) => {
    if (isPopUp) {
      const currentUser = state.user;
      localStorage.setItem(
        "popupName",
        (currentUser && currentUser.profle && currentUser.profile.fname) || ""
      );
      localStorage.setItem(
        "popupEmail",
        (currentUser && currentUser.profle && currentUser.profile.fname) || ""
      );
    } else {
      localStorage.removeItem("popupName");
      localStorage.removeItem("popupEmail");
    }
    logDevice(0);
    setSession(null);
    dispatch({ type: "LOGOUT", payload: { isPopUp } });
  };
  const switchAccount = async (uid, email) => {
    const response = await axios.post(API_PATH.switchAccount, {
      accountId: uid,
      email,
    });
    const { status, data } = response.data;
    if (status === "success") {
      const { token } = data;
      setSession(token);
      logDevice();
      dispatch({
        type: "LOGIN",
        payload: { user: data, isVerifyEmail: true },
      });
      return true;
    }
    throw new Error("An error has occurred! Please try again later.");
  };
  const deleteAccount = async () => {
    const response = await axios.post(API_PATH.accountDelete, {});
    const { status } = response.data;
    if (status === "success") {
      logout();
      return true;
    }
    throw new Error("An error has occurred! Please try again later.");
  };
  const login = async (email, password) => {
    const response = await axios.post(API_PATH.login, {
      email,
      password,
      method: "email",
    });

    const { status, data } = response.data;
    if (status === "success" || status === "password_update_required") {
      const { token, profile } = data;
      setSession(token);
      setEmail(email);
      const isVerifyEmail = parseInt(profile.is_verified, 10) === 1;
      logDevice();
      dispatch({
        type: "LOGIN",
        payload: { user: data, isVerifyEmail },
      });
      initialize();
      return status;
    }
    if (status === "not_found") {
      throw new Error(
        "Invalid email address / password. Please confirm and try again."
      );
    }
    if (status === "suspended") {
      throw new Error(
        "Your account has been suspended! Please contact support for more information."
      );
    }
    throw new Error("An error has occurred! Please try again later.");
  };
  const logDevice = async () => {
    axios.post(API_PATH.saveDevice, {
      name: browserName,
      os: osName,
      deviceId: getUA,
      type: 2,
      token: "",
    });
  };

  const resetPassword = async (code, password) => {
    const response = await axios.post(API_PATH.reset, {
      code,
      password,
    });
    const { status } = response.data;
    if (status !== "success") {
      if (status === "code_expired") {
        throw new Error(
          "The code you entered has expired! Please request a new one and try again."
        );
      } else if (status === "code_used") {
        throw new Error(
          "The code you entered has been used! Please request a new one and try again."
        );
      } else if (status === "code_invalid") {
        throw new Error(
          "The code you entered is invalid! Please request a new one and try again."
        );
      } else if (status === "same_password") {
        throw new Error(
          "The password you provided is the same as your current password! Please enter a new password and try again."
        );
      } else {
        throw new Error("An error occurred! Please try again later.");
      }
    }
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(API_PATH.forgot, {
      email,
    });
    const { status } = response.data;
    if (status !== "success") {
      throw new Error(
        "Your email address is incorrect! Please confirm and try again."
      );
    }
  };
  const saveToken = async (saveToken) => {
    await axios.post(API_PATH.notifications, { saveToken, device: getUA });
  };
  const verifyAccount = async (code) => {
    const response = await axios.post(API_PATH.verifyCode, { code });
    const { status } = response.data;
    if (status !== "success") {
      if (status === "code_expired") {
        throw new Error(
          "The code you entered has expired! Please request a new one and try again."
        );
      } else if (status === "code_invalid") {
        throw new Error(
          "The code you entered is invalid! Please request a new one and try again."
        );
      } else if (status === "code_used") {
        throw new Error(
          "The code you entered has been used! Please request a new one and try again."
        );
      } else {
        throw new Error(
          "An error occurred while attempting to verify you! Please try again later."
        );
      }
    } else {
      initialize();
    }
  };
  const sendVerifyCode = async () => {
    const response = await axios.get(API_PATH.sendCode);
    const { status } = response.data;
    if (status !== "success") {
      throw new Error(
        "An error occurred while attempting to send you a verification code! Please try again later."
      );
    }
  };
  const sendPasswordVerify = async () => {
    const { user } = state;
    const { usertype } = user.user;
    const uid = parseInt(usertype, 10) === 1 ? user.user.lid : user.user.uid;
    const response = await axios.post(API_PATH.profile, {
      generate_set_password: uid,
      type: usertype,
    });
    const { status } = response.data;
    if (status !== "success") {
      throw new Error(
        "We cannot send your set password code at the moment! Please try again later."
      );
    }
  };
  const updateSettings = async (values) => {
    const response = await axios.post(API_PATH.setPhoto, {
      photo: values.photo,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
    }
    if (status === "error") {
      throw new Error("omething went wrong. Please try again later.");
    }
    if (status === "invalid_user") {
      logout();
    }
    if (status === "suspended") {
      throw new Error(
        "Your account has been placed on hold! Please contact support."
      );
    }
  };
  const updateSettingsVerification = async (values) => {
    const response = await axios.post(API_PATH.setVerification, {
      ...values,
      idType: values.idType.code,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
      return;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const updateSettingsPassword = async (values) => {
    const response = await axios.post(API_PATH.setPassword, values);
    const { status } = response.data;
    if (status === "success") {
      initialize();
      return;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const getBanks = async () => {
    const response = await axios.get(API_PATH.getWithdrawalBanks);
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };
  const getAccountName = async (bank, number) => {
    // 1. Try public resolve-account endpoint
    try {
      const response = await axios.post(API_PATH.resolveAccount, {
        bank_code: bank,
        account_number: number,
      });
      const { status, data, account_name } = response.data || {};
      if (status === "success" || status === true) {
        const rawName = account_name || data;
        const name = rawName && typeof rawName === "object" ? rawName.account_name : rawName;
        if (name && typeof name === "string" && name.trim()) {
          return name.trim();
        }
      }
    } catch (err) {
      // fallback to verifyAccount
    }

    // 2. Fallback to authenticated verify_account endpoint
    const response = await axios.post(API_PATH.verifyAccount, {
      bank_code: bank,
      account_number: number,
    });
    const { status, data, account_name, message } = response.data || {};
    if (status === "success" || status === true) {
      const rawName = account_name || data;
      const name = rawName && typeof rawName === "object" ? rawName.account_name : rawName;
      if (name && typeof name === "string" && name.trim()) {
        return name.trim();
      }
    }
    throw new Error(
      message || "We cannot verify your bank account information! Please confirm it is accurate and try again."
    );
  };
  const updateBVN = async (values) => {
    const response = await axios.post(API_PATH.setBVN, {
      ...values,
    });
    const { status, methods, otpSent, reference } = response.data;
    if (status === "success") {
      return { methods, otpSent, reference };
    }
    if (status === "bvn_otp_error") {
      throw new Error(
        "The OTP you entered is invalid! Please confirm and try again."
      );
    }
    if (status === "bvn_name_error") {
      return status;
    }
    throw new Error(
      "There is an issue with your BVN information! Please check your email for more information."
    );
  };

  const bvnLookup = async (bvn) => {
    const response = await axios.post(API_PATH.bvnLookup, {
      bvn: String(bvn).trim(),
    });
    const { status, data, message } = response.data || {};
    if (status === "success") {
      return data || {};
    }
    throw new Error(message || "BVN verification failed. Please verify your 11-digit BVN and try again.");
  };

  const updatePersonalDetails = async (details) => {
    const response = await axios.post(API_PATH.personalDetails, details);
    const { status, message } = response.data || {};
    if (status === "success") {
      initialize();
      return true;
    }
    throw new Error(message || "Failed to update personal details. Please try again.");
  };

  const uploadFacial = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(API_PATH.setPhoto, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const { status, message } = response.data || {};
    if (status === "success") {
      initialize();
      return true;
    }
    throw new Error(message || "Failed to upload facial photo. Please try again.");
  };

  const uploadProofOfAddress = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(API_PATH.setProofOfAddress, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const { status, message } = response.data || {};
    if (status === "success") {
      initialize();
      return true;
    }
    throw new Error(message || "Failed to upload proof of address. Please try again.");
  };

  const uploadIdentificationDoc = async (idType, idNumber, file) => {
    const formData = new FormData();
    const typeCode = typeof idType === "object" ? idType.code : idType;
    formData.append("id_type", typeCode);
    formData.append("id_number", idNumber);
    if (file) {
      formData.append("file", file);
    }
    const response = await axios.post(API_PATH.setVerification, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const { status, message } = response.data || {};
    if (status === "success") {
      initialize();
      return true;
    }
    throw new Error(message || "Failed to upload identification document. Please try again.");
  };
  const updateSettingsNOK = async (values) => {
    const response = await axios.post(API_PATH.setNOK, {
      ...values,
      relationship: values.relationship.code,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
    }
    if (status !== "success") {
      throw new Error(
        "An error occurred while updating next of kin! Please try again later."
      );
    }
  };
  const updateSettingsCompany = async (values) => {
    const response = await axios.post(API_PATH.setCompany, {
      ...values,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
    }
    if (status !== "success") {
      throw new Error(
        "An error occurred while updating your company details! Please try again later."
      );
    }
  };
  const checkMonoAccount = async (code, amount, reason) => {
    const response = await axios.post(API_PATH.verifyBank, {
      code,
      reason,
      amount,
    });
    const { status, data } = response.data;
    if (status === "success") {
      return data.id || "";
    }
    throw new Error(
      "An error occurred while checking your bank account. Please try again later."
    );
  };
  const addTopup = async (ref, amount, method, type) => {
    try {
      const response = await axios.post(API_PATH.topupWallet, {
        amount,
        type,
        method,
        ref,
      });
      const { status, message, duplicate } = response?.data || {};
      if (status === "success") {
        initialize();
        return { status: "success", duplicate: Boolean(duplicate) };
      }
      if (status === "verify_failed") {
        throw new Error("Payment verification failed with Paystack. Please contact support if you were debited.");
      }
      if (status === "too_many_requests" || response?.status === 429) {
        throw new Error(message || "Too many transaction requests submitted. Please wait a few minutes.");
      }
      let errorDetail = message || "An error occurred while verifying your payment. Please try again later or contact support if you were debited.";
      if (status === "not_found") {
        errorDetail = "Payment cannot be verified: Invalid user account.";
      } else if (status === "not_authorized") {
        errorDetail = "Payment cannot be verified: Invalid token or session.";
      }
      throw new Error(errorDetail);
    } catch (err) {
      if (err.message) throw err;
      throw new Error("Topup processing error. Please contact support if debited.");
    }
  };
  const topupSavings = async (ref, amount, method, sid) => {
    const response = await axios.post(API_PATH.topupSavings, {
      amount,
      sid,
      method,
      ref,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
      return;
    }
    throw new Error(
      "An error occurred while verifying your payment. Please try again later or contact support if you were debited."
    );
  };
  const sendSavingLetter = async (savingId) => {
    const response = await axios.get(
      `${API_PATH.getSavingsLetter}?savingId=${savingId}`
    );
    const { status } = response.data;
    if (status === "success") {
      return;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const sendInvestmentLetter = async (investmentId) => {
    const response = await axios.get(
      `${API_PATH.getInvestmentsLetter}?investmentId=${investmentId}`
    );
    const { status } = response.data;
    if (status === "success") {
      return;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const cancelSaving = async (savingId) => {
    const response = await axios.post(API_PATH.cancelSavings, {
      savingId,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
      return;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const sendAccountStatement = async () => {
    const response = await axios.get(API_PATH.getStatement);
    const { status } = response.data;
    if (status === "success") {
      return true;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const sendPortfolioStatement = async () => {
    const response = await axios.get(API_PATH.getPortfolio);
    const { status } = response.data;
    if (status === "success") {
      return true;
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const cancelInvestment = async (investmentId) => {
    const response = await axios.post(API_PATH.cancelInvestments, {
      investmentId,
      email: getEmail(),
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
      return;
    }
    if (status === "pending") {
      throw new Error(
        "Your termination request has been sent to other signatories for approval! We'll notify you once the request has been approved."
      );
    }
    throw new Error("Something went wrong. Please try again later.");
  };
  const addWithdraw = async (values) => {
    const actionKey = "wallet_withdraw";
    const idempotencyKey = IdempotencyManager.getOrCreateKey(actionKey);
    const reasonCode = (values.reason && (values.reason.code || values.reason.name)) || values.reason || "";
    const maxRetries = 3;

    const delay = (ms) =>
      new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, ms);
      });

    const executeAttempt = async (attempt) => {
      let response;
      try {
        response = await axios.post(
          API_PATH.withdrawWallet,
          {
            amount: values.amount,
            reason: reasonCode,
            note: values.note || "",
            password: values.password,
            email: getEmail(),
          },
          {
            headers: {
              "Idempotency-Key": idempotencyKey,
            },
          }
        );
      } catch (err) {
        // Handle network timeout/drop mid-flight with exponential backoff using SAME key
        if (attempt < maxRetries) {
          await delay(2000 * attempt);
          return executeAttempt(attempt + 1);
        }
        throw new Error(
          err.message || "Network connection failed while processing withdrawal. Please check your connection and try again."
        );
      }

      const httpStatus = response?.status;
      const responseData = response?.data || {};
      const { status, message } = responseData;

      // Handle HTTP 409 Conflict (Concurrent or in-flight processing)
      if (httpStatus === 409 || status === "conflict" || status === "processing") {
        if (attempt < maxRetries) {
          // Wait 2.5 seconds and retry query with the SAME key
          await delay(2500);
          return executeAttempt(attempt + 1);
        }
        // Retain key in session so subsequent retries or checks match the same transaction
        throw new Error(
          "Your withdrawal request is currently processing. Please check your transaction history shortly."
        );
      }

      // Handle Rate Limiter (HTTP 429)
      if (httpStatus === 429 || status === "too_many_requests") {
        throw new Error(
          message || "Too many withdrawal requests submitted. Please wait a few minutes before trying again."
        );
      }

      // Handle Success
      if (status === "success") {
        IdempotencyManager.clearKey(actionKey);
        initialize();
        return { status: "success" };
      }

      // Handle Joint Accounts (Requires co-signatory approval)
      if (status === "pending") {
        IdempotencyManager.clearKey(actionKey);
        initialize();
        return {
          status: "pending",
          message:
            "Your withdrawal request has been sent to other signatories for approval! We'll notify you once the request has been approved.",
        };
      }

      // Handle Client Business Validation Errors (Clear key so user can correct parameters with new key)
      if (status === "invalid_account") {
        IdempotencyManager.clearKey(actionKey);
        throw new Error("No Withdrawal Account detected! Please set a valid account to proceed.");
      }
      if (status === "amount_too_small") {
        IdempotencyManager.clearKey(actionKey);
        throw new Error("The withdrawal amount is lower than the minimum amount! Please set a valid amount to proceed.");
      }
      if (status === "insufficient_balance") {
        IdempotencyManager.clearKey(actionKey);
        throw new Error("You do not have enough balance in your wallet to withdraw.");
      }
      if (status === "interval_not_reached") {
        IdempotencyManager.clearKey(actionKey);
        throw new Error("You cannot request withdrawal at the moment! 12 hours must pass between withdrawal requests.");
      }
      if (status === "verification_error") {
        IdempotencyManager.clearKey(actionKey);
        const err = new Error("Please complete your KYC before requesting withdrawal");
        err.code = "VERIFICATION_ERROR";
        throw err;
      }
      if (status === "invalid_password") {
        IdempotencyManager.clearKey(actionKey);
        const err = new Error("Your password is incorrect! Please confirm and try again.");
        err.code = "INVALID_PASSWORD";
        throw err;
      }

      // Empty response (e.g. axios errInterceptor resolved empty on network drop)
      if (!httpStatus && !status) {
        if (attempt < maxRetries) {
          await delay(2000 * attempt);
          return executeAttempt(attempt + 1);
        }
        throw new Error("Network connection failed while processing withdrawal. Please check your connection and try again.");
      }

      // Generic 400 Bad Request
      if (httpStatus === 400) {
        IdempotencyManager.clearKey(actionKey);
        throw new Error(message || "Withdrawal validation failed. Please check your details and try again.");
      }

      // Unexpected response - retry if attempts remain
      if (attempt < maxRetries) {
        await delay(2000 * attempt);
        return executeAttempt(attempt + 1);
      }

      throw new Error(
        message || "Something went wrong. Please try again later or contact support if your wallet is debited."
      );
    };

    return executeAttempt(1);
  };


  const addWithdrawAccount = async (values) => {
    const payload = {
      bank_code: values.bank?.code || values.bank_code || "",
      account_number: values.accountNumber || values.account_number || "",
      bank_name: values.bank?.name || values.bank_name || "",
      account_name: values.accountName || values.account_name || "",
    };

    // Attempt saving to user bank-account endpoint as well
    try {
      await axios.post(API_PATH.saveBankAccount, payload);
    } catch (err) {
      // non-blocking sync to user bank-account endpoint
    }

    const response = await axios.post(API_PATH.saveAccount, payload);
    const { status, message } = response.data || {};
    if (status === "success") {
      initialize();
      return;
    }
    throw new Error(message || `Something went wrong. Please try again later.`);
  };
  const addSaving = async (values) => {
    const response = await axios.post(API_PATH.saveSavings, {
      name: values.name,
      duration: values.duration,
      amount: values.amount,
      type: values.type,
      frequency: values.frequency,
      method: values.method,
      ref: values.ref,
      paid: values.paid,
      isLoan: values.isLoan,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
    } else if (status === "verify_failed") {
      throw new Error(
        `Something went wrong! Please try again later or contact us if you were debited.`
      );
    } else if (status === "insufficient_balance") {
      throw new Error(
        `You do not have sufficient balance in your wallet to pay for this! Please use another payment option to proceed.`
      );
    } else {
      throw new Error(
        `Something went wrong! Please try again later or contact us if you were debited.`
      );
    }
  };
  const saveRiskProfile = async (values) => {
    const response = await axios.post(API_PATH.investorProfile, values);
    const { status } = response.data;
    if (status === "success") {
      initialize();
    } else {
      throw new Error(`Something went wrong! Please try again later.`);
    }
  };
  const payBill = async (values) => {
    const response = await axios.post(API_PATH.payBill, {
      ...values,
      type: values.type.code,
      network:
        (values.network &&
          (values.network.code || values.network.biller_name)) ||
        "",
      plan: (values.plan && values.plan.biller_name) || "",
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
    } else if (status === "insufficient_balance") {
      throw new Error(
        "You do not have enough balance in your wallet to complete this request. Please top up your wallet to proceed."
      );
    } else if (status === "verification_error") {
      throw new Error("Please complete your KYC before requesting purchase.");
    } else if (status === "invalid_password") {
      throw new Error(
        "Your password is incorrect! Please confirm and try again."
      );
    } else {
      throw new Error(
        "Something went wrong. Please try again later or contact us if your wallet is debited."
      );
    }
  };
  const verifyBill = async (type, itemCode, code, customer) => {
    const response = await axios.post(API_PATH.verifyBill, {
      itemCode,
      code,
      customer,
    });
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    throw new Error(
      type === "dstv"
        ? "We cannot validate your Smart Card Number! Please confirm it is accurate and try again later."
        : "We cannot validate your Meter Number! Please confirm it is accurate and try again later."
    );
  };
  const getBeneficiaries = async (type) => {
    const response = await axios.get(
      `${API_PATH.getBillBeneficiaries}?type=${type}`
    );
    const { status, data } = response.data;
    if (status === "success") {
      if (data.length === 0) {
        throw new Error("No beneficiary added for this bill type.");
      }
      return data;
    }
    throw new Error("An error occured! Please try again later.");
  };
  const addInvestments = async (uid) => {
    const response = await axios.post(API_PATH.saveInvestments, {
      uid,
    });
    const { status } = response.data;
    if (status === "success") {
      initialize();
      return;
    }
    throw new Error(`Something went wrong! Please try again later.`);
  };
  const getSaving = async (sid) => {
    const response = await axios.get(`${API_PATH.savingDetails}?saving=${sid}`);
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    throw new Error(
      `An error occurred while fetching your saving details! Please try again later.`
    );
  };
  const getInvestment = async (pid) => {
    const response = await axios.get(
      `${API_PATH.investmentDetails}?investment=${pid}`
    );
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    throw new Error(
      `An error occurred while fetching your investment details! Please try again later.`
    );
  };
  const getDefaults = async () => {
    const response = await axios.get(API_PATH.getDefaults);
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };
  const getSavingsPlans = async () => {
    const response = await axios.get(API_PATH.getSavingsPlans);
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };
  const getInvestTypes = async (uid) => {
    const response = await axios.get(
      `${API_PATH.getInvestmentsTypes}?uid=${uid}`
    );
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };
  const getInvestCategories = async () => {
    const response = await axios.get(API_PATH.getInvestmentsCategories);
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };

  const fetchNetwork = async (type, code) => {
    const response = await axios.get(
      `${API_PATH.getBillNetworks}?type=${type}&code=${code}`
    );
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };
  const fetchBeneficiaries = async (type) => {
    const response = await axios.get(
      `${API_PATH.getBillBeneficiaries}?type=${type}`
    );
    const { status, data } = response.data;
    if (status === "success") {
      return data;
    }
    return [];
  };

  const getInvestmentProducts = async (categoryId) => {
    try {
      const response = await axios.get(
        `${API_PATH.getInvestmentsProducts}?category_id=${categoryId}`
      );
      const { status, data } = response.data;
      if (status === "success") {
        return data || [];
      }
      return [];
    } catch (e) {
      return [];
    }
  };

  const bookInvestment = async (params) => {
    const { productId, units, paymentType, reference, password, tenor, customDate } = params;

    let tenorDateStr = tenor;
    if (tenor === "custom" && customDate) {
      tenorDateStr = customDate;
    } else if (tenor === "6_months" || tenor === "1_year" || !String(tenor).includes("-")) {
      const d = new Date();
      if (tenor === "1_year") {
        d.setFullYear(d.getFullYear() + 1);
      } else {
        d.setMonth(d.getMonth() + 6);
      }
      tenorDateStr = d.toISOString().substring(0, 10);
    }

    const response = await axios.post(API_PATH.addInvestment, {
      productId,
      units,
      payment_type: paymentType,
      reference: reference || "",
      password,
      tenor: tenorDateStr,
      custom_date: customDate || "",
    });

    const { status, message } = response.data;
    if (status === "success") {
      initialize();
      return true;
    }
    if (status === "insufficient_balance") {
      throw new Error("Insufficient wallet balance to pay for this investment. Please top up your wallet or select another payment option.");
    }
    if (status === "invalid_password") {
      throw new Error("Your account password is incorrect! Please verify and try again.");
    }
    throw new Error(message || "Failed to book investment. Please try again later.");
  };

  const topupInvestment = async (params) => {
    const { investmentId, units, paymentType, reference, password } = params;
    const response = await axios.post(API_PATH.topupInvestments, {
      investmentId,
      units,
      payment_type: paymentType,
      reference: reference || "",
      password,
    });

    const { status, message } = response.data;
    if (status === "success") {
      initialize();
      return true;
    }
    if (status === "insufficient_balance") {
      throw new Error("Insufficient wallet balance to pay for this investment top up. Please top up your wallet or select another payment option.");
    }
    if (status === "invalid_password") {
      throw new Error("Your account password is incorrect! Please verify and try again.");
    }
    throw new Error(message || "Failed to top up investment. Please try again later.");
  };

  return (
    <AuthContext.Provider
      // eslint-disable-next-line
      value={{
        ...state,
        method: "jwt",
        getInvestCategories,
        getInvestmentProducts,
        bookInvestment,
        topupInvestment,
        saveToken,

        login,
        logout,
        updateSettings,
        sendPasswordVerify,
        getBanks,
        getAccountName,
        forgotPassword,
        resetPassword,
        updateSettingsNOK,
        updateSettingsCompany,
        addSaving,
        saveRiskProfile,
        sendVerifyCode,
        verifyAccount,
        updateBVN,
        initialize,
        getDefaults,
        updateSettingsVerification,
        updateSettingsPassword,
        addTopup,
        checkMonoAccount,
        cancelSaving,
        cancelInvestment,
        addWithdraw,
        getSavingsPlans,
        addWithdrawAccount,
        getInvestTypes,
        addInvestments,
        fetchNetwork,
        fetchBeneficiaries,
        verifyBill,
        payBill,
        getBeneficiaries,
        getSaving,
        getInvestment,
        topupSavings,
        sendInvestmentLetter,
        sendSavingLetter,
        deleteAccount,
        switchAccount,
        sendAccountStatement,
        sendPortfolioStatement,
        bvnLookup,
        updatePersonalDetails,
        uploadFacial,
        uploadProofOfAddress,
        uploadIdentificationDoc,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}


export { AuthContext, AuthProvider };
